/**
 * @fileoverview Zod schemas for store types.
 * @module features/settings/schemas/storeSchema
 */

import { z } from 'zod'

/**
 * Platform types for e-commerce stores.
 */
export const PlatformTypeSchema = z.enum(['SHOPIFY', 'WOOCOMMERCE'])

export type PlatformType = z.infer<typeof PlatformTypeSchema>

/**
 * Connection modes for stores.
 */
export const ConnectionModeSchema = z.enum(['DIRECT', 'ATTRIBUTION_ONLY'])

export type ConnectionMode = z.infer<typeof ConnectionModeSchema>

/**
 * Store DTO from the backend.
 */
export const StoreSchema = z.object({
  id: z.number(),
  name: z.string(),
  platformType: PlatformTypeSchema.nullable(),
  platformIdentifier: z.string().nullable(),
  platformDomain: z.string().nullable(),
  integrationId: z.string().uuid().nullable(),
  attributionEnabled: z.boolean(),
  connectionMode: ConnectionModeSchema.nullable(),
  createdAt: z.string(),
})

export type Store = z.infer<typeof StoreSchema>

export const StoreArraySchema = z.array(StoreSchema)

/**
 * Request to create a new store.
 */
export const CreateStoreRequestSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  platformType: PlatformTypeSchema,
  platformIdentifier: z.string().nullable().optional(),
  platformDomain: z.string().nullable().optional(),
  integrationId: z.string().uuid().nullable().optional(),
  attributionEnabled: z.boolean().default(true),
})

export type CreateStoreRequest = z.infer<typeof CreateStoreRequestSchema>

/**
 * Request to update an existing store.
 */
export const UpdateStoreRequestSchema = z.object({
  name: z.string().min(1).nullable().optional(),
  platformDomain: z.string().nullable().optional(),
  attributionEnabled: z.boolean().nullable().optional(),
})

export type UpdateStoreRequest = z.infer<typeof UpdateStoreRequestSchema>

/**
 * Request to update store connection mode.
 */
export const UpdateConnectionModeRequestSchema = z.object({
  connectionMode: ConnectionModeSchema,
})

export type UpdateConnectionModeRequest = z.infer<typeof UpdateConnectionModeRequestSchema>
